import clsx from "clsx";
import { gql } from "graphql-request";
import NextLink from "next/link";
import { FC, useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";
import { Image } from "components/Image/Image";
import { GeneralRoutingLinkButton } from "components/Molecules/GeneralRoutingLinkButton";
import { PipelineRoutingLinkButton } from "components/Molecules/PipelineRoutingLinkButton";
import { useScrollTop } from "hooks";
import { useScreenIsAboveBreakpoint } from "hooks/useScreenIsAboveBreakpoint";
import { Close, Hamburg } from "icons";
import { ComponentIds } from "types/enums/componentIds";
import { DataTestId } from "types/enums/testId";
import { MegaNavigationContent } from "types/graphql/generated";
import {
  MegaNavMobileContextProvider,
  useMegaNavMobileContext,
} from "./hooks/useMegaNavMobileContext/useMegaNavMobileContext";
import { MegaMenuResources } from "./MegaMenuResources";
import { MegaMenuResourcesMobile } from "./MegaMenuResourcesMobile";

interface MegaNavigationProps {
  content: MegaNavigationContent;
}

const MegaNavigation: FC<MegaNavigationProps> = ({ ...props }) => {
  return (
    <MegaNavMobileContextProvider>
      <MegaNavigationInner {...props} />
    </MegaNavMobileContextProvider>
  );
};

const MegaNavigationInner: FC<MegaNavigationProps> = ({
  content: {
    loginButton,
    getStartedButton,
    menuLogo: {
      data: { image },
    },
    navResources,
  },
}) => {
  const [subMenuHeight, setSubMenuHeight] = useState<number>(0);
  const { setMobileMenuOpen, mobileMenuOpen } = useMegaNavMobileContext();
  const isDesktopView = useScreenIsAboveBreakpoint("lg");
  const scrollTop = useScrollTop();

  const stateClasses = clsx({
    shadow: mobileMenuOpen || (scrollTop > 56 && subMenuHeight < 1),
    "border-fog-20 bg-fog-00 bg-opacity-90": subMenuHeight > 0 && isDesktopView,
    "fixed bg-opacity-90 backdrop-blur-xl	backdrop-saturate-[1.2]": mobileMenuOpen || scrollTop > 56,
    "bg-fog-00 bg-opacity-100": mobileMenuOpen && isDesktopView === false,
  });

  useEffect(() => {
    if (isDesktopView) {
      setMobileMenuOpen(false);
    }
  }, [isDesktopView, setMobileMenuOpen]);

  return (
    // this top level div provides a relative container for the nav to contain to. This is necessary to ensure the nav is positioned correctly when a banner is present.
    <div className="relative h-full w-full">
      <nav
        className={twMerge(
          "absolute left-0 right-0 top-0 z-[100] h-navbar items-stretch border-b-[1px] border-transparent bg-fog-00 bg-opacity-0 transition-all duration-500 ease-in-out lg:h-navbar-lg",
          stateClasses
        )}
        id={ComponentIds.MegaNavigation}
      >
        <menu className="m-auto flex h-navbar w-full max-w-screen-xl flex-row justify-between gap-3 px-6 lg:h-navbar-lg lg:px-8">
          <div className="flex gap-4">
            <NextLink href="/" className="flex h-full">
              <Image {...image} width={100} height={32} className="h-full" placeholder="empty" />
            </NextLink>

            <MegaMenuResources
              // @ts-ignore  entryLabel, is missing, but required for the content editor workflow in strapi, not required for the frontend
              navResources={navResources}
              subMenuHeightCallback={(height) => setSubMenuHeight(height)}
              // hidden on mobile
              className="hidden lg:block"
            />

            <MegaMenuResourcesMobile
              // @ts-ignore  entryLabel, is missing, but required for the content editor workflow in strapi, not required for the frontend
              navResources={navResources}
              loginButton={loginButton}
              getStartedButton={getStartedButton}
              isOpen={mobileMenuOpen}
              onClose={() => setMobileMenuOpen(false)}
              logoImageAttr={image}
              // hidden on desktop
              className="block lg:hidden"
            />
          </div>
          <div className="flex gap-4 py-2 lg:py-4">
            <GeneralRoutingLinkButton
              compact
              narrow
              href={loginButton?.url}
              // hidden on mobile
              className="hidden bg-transparent px-2 text-inherit hover:bg-transparent hover:text-fog-50 focus:bg-transparent focus:text-fog-50 lg:block"
              dataTestId={DataTestId.Header1LoginButton}
            >
              {loginButton?.label}
            </GeneralRoutingLinkButton>

            {!mobileMenuOpen && (
              <PipelineRoutingLinkButton
                compact
                narrow
                href={getStartedButton?.url}
                dataTestId={DataTestId.Header1GetStartedButton}
              >
                {getStartedButton?.label}
              </PipelineRoutingLinkButton>
            )}

            <button
              type="button"
              // hidden on desktop
              className="-mx-2 block h-full w-auto px-2 lg:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <Close className="h-5 w-5 fill-current" /> : <Hamburg className="h-6 w-6" />}
            </button>
          </div>
        </menu>
      </nav>
    </div>
  );
};

gql`
  fragment LinkContent on ComponentAtomsLink {
    id
    label
    url
  }

  fragment BillboardContent on ComponentMoleculesBillboard {
    title
    subTitle
    graphic {
      data {
        attributes {
          hash
          alternativeText
        }
      }
    }
    primaryButton {
      ...LinkContent
    }
    secondaryButton {
      ...LinkContent
    }
  }
  fragment BrickContent on ComponentMoleculesBrick {
    id
    title
    titleTag {
      label
      bgColor
      icon {
        data {
          attributes {
            hash
            alternativeText
          }
        }
      }
    }
    subTitle
    hoverEffect
    url
    imageSize
    image {
      data {
        attributes {
          hash
          alternativeText
        }
      }
    }
  }

  fragment MegaNavigationContent on ComponentOrganismsMegaNavigation {
    menuLogo: logo {
      data {
        image: attributes {
          src: hash
          alt: alternativeText
        }
      }
    }
    navResources {
      id
      label
      url
      subMenuDesktopLayout
      subMenuTabs {
        id
        tabBrick {
          ...BrickContent
        }
        entryLabel
        linkedPanels {
          id
          bgColorType
          title
          brickList {
            bricks {
              ...BrickContent
            }
            secondaryButton {
              ...LinkContent
            }
          }
          featureListWithBillboard {
            links {
              ...LinkContent
            }
            billboard {
              ...BillboardContent
            }
          }
        }
      }
      subMenuPanels {
        id
        bgColorType
        title
        brickList {
          bricks {
            ...BrickContent
          }
          secondaryButton {
            ...LinkContent
          }
        }
        featureListWithBillboard {
          links {
            ...LinkContent
          }
          billboard {
            ...BillboardContent
          }
        }
      }
    }
    loginButton {
      ...LinkContent
    }
    getStartedButton {
      ...LinkContent
    }
  }
`;

export { MegaNavigation };
export type { MegaNavigationProps };
